import { Box } from '@chakra-ui/react';
import { Markdown } from '@tiptap/markdown';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { useEffect, type CSSProperties, type RefObject } from 'react';

import { MessageMarkdownMentionNode } from '#components/message-markdown-mention/message-markdown-mention';
import {
  getMessageMentionMenuItems,
  INACTIVE_MESSAGE_MENTION_MENU_STATE,
  MESSAGE_MENTION_MENU_PLUGIN_KEY,
  MessageMentionMenuExtension,
} from '#components/message-markdown-mention-menu/message-markdown-mention-menu';
import { MessageMarkdownMentionMenuPopover } from '#components/message-markdown-mention-menu-popover/message-markdown-mention-menu-popover';
import {
  INACTIVE_MESSAGE_MARKDOWN_SLASH_MENU_STATE,
  MESSAGE_MARKDOWN_SLASH_MENU_PLUGIN_KEY,
  MessageMarkdownSlashMenuExtension,
} from '#components/message-markdown-slash-menu/message-markdown-slash-menu';
import { MessageMarkdownSlashMenuPopover } from '#components/message-markdown-slash-menu-popover/message-markdown-slash-menu-popover';

import './message-markdown-editor.scss';

export interface MessageMarkdownEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  onSubmit?: () => void | Promise<void>;
  submitOnEnter?: boolean;
  autoFocus?: boolean;
  rootRef?: RefObject<HTMLDivElement | null>;
  minHeightPx?: number;
  maxHeightPx?: number;
  ariaLabel?: string;
}

export function MessageMarkdownEditor({
  value,
  onChange,
  placeholder,
  disabled = false,
  onSubmit,
  submitOnEnter = true,
  autoFocus = false,
  rootRef,
  minHeightPx = 40,
  maxHeightPx = 180,
  ariaLabel,
}: MessageMarkdownEditorProps) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        link: {
          openOnClick: false,
          autolink: true,
          linkOnPaste: true,
        },
      }),
      Markdown.configure({
        markedOptions: { gfm: true },
      }),
      MessageMarkdownMentionNode,
      MessageMentionMenuExtension,
      MessageMarkdownSlashMenuExtension,
    ],
    content: value,
    contentType: 'markdown',
    editable: !disabled,
    immediatelyRender: false,
    editorProps: {
      attributes: {
        class: 'message-markdown-editor__content',
        'data-chat-composer-text': 'true',
        'data-placeholder': placeholder ?? '',
        'aria-label': ariaLabel ?? placeholder ?? 'Message editor',
      },
      handleKeyDown(view, event) {
        if (
          submitOnEnter &&
          onSubmit &&
          event.key === 'Enter' &&
          !event.shiftKey
        ) {
          const slashMenuState =
            MESSAGE_MARKDOWN_SLASH_MENU_PLUGIN_KEY.getState(view.state) ??
            INACTIVE_MESSAGE_MARKDOWN_SLASH_MENU_STATE;
          if (slashMenuState.active) {
            return false;
          }

          const mentionMenuState =
            MESSAGE_MENTION_MENU_PLUGIN_KEY.getState(view.state) ??
            INACTIVE_MESSAGE_MENTION_MENU_STATE;
          const mentionItems =
            editor != null ? getMessageMentionMenuItems(editor) : [];
          if (mentionMenuState.active && mentionItems.length > 0) {
            return false;
          }

          event.preventDefault();
          void onSubmit();
          return true;
        }

        return false;
      },
    },
    onCreate: ({ editor: currentEditor }) => {
      if (autoFocus) {
        currentEditor.commands.focus('end');
      }
    },
    onUpdate: ({ editor: currentEditor }) => {
      onChange(currentEditor.getMarkdown());
    },
  });

  useEffect(() => {
    if (!editor) {
      return;
    }

    editor.setEditable(!disabled);
  }, [disabled, editor]);

  useEffect(() => {
    if (!editor) {
      return;
    }

    const currentValue = editor.getMarkdown();
    if (currentValue === value) {
      return;
    }

    editor.commands.setContent(value, {
      contentType: 'markdown',
      emitUpdate: false,
    });
  }, [editor, value]);

  return (
    <Box ref={rootRef} className="message-markdown-editor" width="100%">
      <Box
        className="message-markdown-editor__surface"
        width="100%"
        data-empty={value.trim().length === 0 ? 'true' : 'false'}
        data-placeholder={placeholder ?? ''}
      >
        <MessageMarkdownMentionMenuPopover editor={editor} />
        <MessageMarkdownSlashMenuPopover editor={editor} />
        <Box
          style={
            {
              ['--message-markdown-editor-min-height' as string]: `${minHeightPx}px`,
              ['--message-markdown-editor-max-height' as string]: `${maxHeightPx}px`,
            } as CSSProperties
          }
        >
          <EditorContent editor={editor} />
        </Box>
      </Box>
    </Box>
  );
}
